// Formato y armado de datos de los reportes. Funciones puras (sin React) para poder probarlas.

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** "2026-10-21" -> Date local a mediodía (evita que un huso horario la corra de día). */
export const parseDay = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d, 12);
};

/** 21/10/2026 */
export const formatDay = (iso) => {
  const d = parseDay(iso);
  return d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : '-';
};

/** 21/10/2026 04:31 (el servidor ya trabaja en hora de Montevideo). */
export const formatDateTime = (iso) => {
  if (!iso) return '-';
  const [date, time = ''] = String(iso).split('T');
  return `${formatDay(date)} ${time.slice(0, 5)}`.trim();
};

/** Etiqueta corta de un punto de la serie según su granularidad: "21 oct", "sem 19 oct" o "oct 2026". */
export const periodLabel = (iso, granularity) => {
  const d = parseDay(iso);
  if (!d) return '';
  if (granularity === 'month') return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return granularity === 'week' ? `sem ${base}` : base;
};

/** 1.284 -> "1,3 mil"; 12.900.000 -> "12,9 M". Para ejes y tarjetas: los valores exactos van en el tooltip y la tabla. */
export const compact = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const abs = Math.abs(n);
  const one = (x) => (Math.round(x * 10) / 10).toString().replace('.', ',');
  if (abs >= 1e9) return `${one(n / 1e9)} MM`;
  if (abs >= 1e6) return `${one(n / 1e6)} M`;
  if (abs >= 1e4) return `${one(n / 1e3)} mil`;
  return Math.round(n).toLocaleString('es-UY');
};

/** Entero con separador de miles ("1.284"). */
export const count = (value) => (Number.isFinite(Number(value)) ? Math.round(Number(value)).toLocaleString('es-UY') : '-');

/** Techo "redondo" para el eje: 0 / 25 / 50 / 75 / 100. Devuelve { max, ticks }. */
export const niceScale = (maxValue, steps = 4) => {
  if (!(maxValue > 0)) return { max: 1, ticks: [0, 1] };
  const rough = maxValue / steps;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const unit = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((u) => u >= rough);
  const max = unit * Math.ceil(maxValue / unit);
  const ticks = [];
  for (let v = 0; v <= max + unit / 1000; v += unit) ticks.push(Math.round(v * 1e6) / 1e6);
  return { max, ticks };
};

/** Estado de una empresa según los días sin actividad: { key, label } (el color lo pone la pantalla). */
export const activityStatus = (daysSinceActivity, isActive = true) => {
  if (!isActive) return { key: 'inactive', label: 'Dada de baja' };
  if (daysSinceActivity === null || daysSinceActivity === undefined) return { key: 'never', label: 'Sin actividad' };
  if (daysSinceActivity > 14) return { key: 'dormant', label: `Dormida (${daysSinceActivity} d)` };
  if (daysSinceActivity > 3) return { key: 'slow', label: `Baja (${daysSinceActivity} d)` };
  return { key: 'ok', label: 'Activa' };
};

/** Tasa de rechazo de CFE en %, o null si no emitió ninguno. */
export const rejectionRate = (emitted, rejected) => (emitted > 0 ? (rejected / emitted) * 100 : null);

/** Texto de los días del selector: "Últimos 30 días". */
export const RANGE_OPTIONS = [
  { value: 7, label: 'Últimos 7 días' },
  { value: 30, label: 'Últimos 30 días' },
  { value: 90, label: 'Últimos 90 días' },
  { value: 365, label: 'Últimos 12 meses' },
];

/** Granularidad sugerida para un rango, para no dibujar 365 columnas. */
export const defaultGranularity = (days) => (days <= 31 ? 'day' : days <= 120 ? 'week' : 'month');

/** Días de atraso de los datos respecto de hoy (0 = al día de anoche). Null si no hay datos. */
export const staleDays = (dataAsOf, today = new Date()) => {
  const d = parseDay(dataAsOf);
  if (!d) return null;
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12);
  return Math.max(0, Math.round((t - d) / 86400000) - 1);
};

/** Tamaño de una base: MB hasta 1.024 y GB desde ahí ("412,5 MB", "2,2 GB"). */
export const formatSize = (mb) => {
  const n = Number(mb);
  if (!Number.isFinite(n)) return '-';
  const one = (x) => (Math.round(x * 10) / 10).toLocaleString('es-UY', { maximumFractionDigits: 1 });
  return n >= 1024 ? `${one(n / 1024)} GB` : `${one(n)} MB`;
};
